import { faTruck } from '@fortawesome/free-solid-svg-icons';

/**
 * Delivery walkthrough.
 *
 * The DeliveryScreen + DeliveryForm pair is shared by Cargo and Bunkers
 * deliveries; the form structure is identical across commodities (the
 * difference is just which recaps you can link).
 *
 * Layout note: deliveries use a What / Who / How tab strip (NOT a wizard
 * stepper) and only one tab's content is mounted at a time. The Who tab has
 * its own side nav with Business Partners and Contract/Legal sub-sections.
 * The header carries a floating Action button whose menu opens Documents,
 * Checklist, Finance Calculator, Invoices and Logs panels.
 *
 * The engine's ``clickBefore`` hook switches tabs and section nav items the
 * way a real user would, so each step has a guaranteed-mounted target.
 */

const NEW_DELIVERY_ROUTE = '/deliveries/new';

// Section nav items expose only their `title` attribute as a stable hook;
// the items render in the order [business-partners, contract-legal] from
// DeliverySectionNav.js. We anchor on the title so the selectors do not
// break if the order changes.
const SECTION_NAV_BUSINESS_PARTNERS = '.delivery-section-nav-item[title="Business Partners"]';
const SECTION_NAV_CONTRACT_LEGAL = '.delivery-section-nav-item[title="Contract/Legal"]';

const deliveryTour = {
    id: 'delivery',
    title: 'Delivery',
    description:
        'Walk through the delivery form: What (product + recap linkage), Who (Business Partners and Contract/Legal), How (vessel, ports, dates) and the Actions panel for Documents, Checklist, Finance, Invoices and Logs.',
    estimatedMinutes: 5,
    startRoute: NEW_DELIVERY_ROUTE,
    icon: faTruck,
    group: 'flow',
    steps: [
        {
            target: '.delivery-screen',
            title: 'New Delivery',
            content:
                'A delivery is how a recap actually executes: vessel, load and discharge ports, dates, and the link back to the recap(s) it fulfils. One delivery can serve multiple paired recaps.',
            placement: 'center',
            disableBeacon: true,
        },
        {
            target: '.delivery-header-back-button',
            title: 'Back to Trade Ops',
            content:
                'Leaving with unsaved changes prompts you to save a draft first. Drafts appear under the Deliveries tab in Trade Ops.',
            placement: 'bottom',
        },
        {
            target: '.delivery-tabs-and-subnav-container',
            title: 'What / Who / How',
            content:
                'Three folders organise the form: What (the product and which recap is being delivered), Who (partners and contracts) and How (vessel, ports, dates). Click any tab to switch.',
            placement: 'bottom',
        },
        {
            target: '.delivery-tab-content.delivery-tab-what',
            title: 'What - product and recap linkage',
            content:
                'This tab is the entry point. Add the recap(s) this delivery fulfils, set the agreed quantity, and capture any quality clauses. The product details flow in from the linked recap.',
            placement: 'top',
            clickBefore: '.delivery-tab.delivery-tab-purple',
        },
        {
            target: '.recap-card-empty, .recap-card-flip',
            title: 'Link a recap',
            content:
                'Click Add Recap to attach the purchase or sales recap that this delivery fulfils. Linking a recap pulls in product, quantity, price and Incoterms automatically.',
            placement: 'top',
            skipIfMissing: true,
        },
        {
            target: '.delivery-tab-content.delivery-tab-who',
            title: 'Who - partners and contracts',
            content:
                'The Who tab has two sub-sections, switched via the side nav on the right: Business Partners and Contract/Legal. Most fields are read-only mirrors of the linked recap.',
            placement: 'top',
            clickBefore: '.delivery-tab.delivery-tab-green',
        },
        {
            target: '#business-partners',
            title: 'Business Partners',
            content:
                'Buyers, sellers, agents and brokers on the delivery. The default selections come from the linked recap; override here only when the delivery has its own counterparties (e.g. a sub-charter).',
            placement: 'top',
            clickBefore: SECTION_NAV_BUSINESS_PARTNERS,
        },
        {
            target: '#contract-legal',
            title: 'Contract / Legal',
            content:
                'Contract references, governing law, signing dates and any side letters. This is the audit trail layer that links the operational delivery back to the legal contract.',
            placement: 'top',
            clickBefore: SECTION_NAV_CONTRACT_LEGAL,
        },
        {
            target: '.delivery-tab-content.delivery-tab-how',
            title: 'How - vessel, ports, dates',
            content:
                'Vessel search, loading and discharge ports, laycan, charter party date, freight and demurrage terms. This is the operational core of the delivery and feeds the Shipment Overview map.',
            placement: 'top',
            clickBefore: '.delivery-tab.delivery-tab-orange',
        },
        {
            target: '.action-button-container',
            title: 'Actions panel',
            content:
                'The Actions button opens a menu of side panels for everything supporting the delivery: documents, checklist, finance calculator, invoices and a full audit log of changes. We will open each one in turn.',
            placement: 'bottom',
        },
        {
            // First panel: open the action menu, then click Docs. The
            // menu item handler also closes the menu, leaving only the
            // DocumentPanel visible.
            target: '.document-panel',
            title: 'Documents panel',
            content:
                'Vessel paperwork, BDNs, COQs and any contract supporting documents. Drag files in or upload from your computer. The badge on the Docs button in the Actions menu shows how many docs are attached.',
            placement: 'left',
            clickBefore: [
                '.action-button-container .action-button',
                '.menu-item.documents-button',
            ],
            skipIfMissing: true,
        },
        {
            // Subsequent panels: close the previous panel first, then
            // re-open the action menu and click the next item.
            target: '.checklist-panel',
            title: 'Checklist panel',
            content:
                'Operational sign-offs the delivery has to clear before settlement. Each item is a discrete step (vessel arrived, BDN signed, samples taken, etc.). The percentage badge tracks completion and feeds back into the delivery status.',
            placement: 'left',
            clickBefore: [
                '.document-panel .close-button',
                '.action-button-container .action-button',
                '.menu-item.checklist-button',
            ],
            skipIfMissing: true,
        },
        {
            target: '.fc-container',
            title: 'Finance Calculator',
            content:
                'Live cashflow analysis for the delivery: actuals against forecasts, partner-by-partner breakdown of receivables and payables, and a margin view. Refreshes from the linked invoices.',
            placement: 'center',
            clickBefore: [
                '.checklist-panel .close-button',
                '.action-button-container .action-button',
                '.menu-item.cashflow-button',
            ],
            skipIfMissing: true,
        },
        {
            target: '.invoice-panel',
            title: 'Invoices panel',
            content:
                'Link existing invoices to the delivery or issue new ones from here. Each invoice carries its own status (Draft -> Issued -> Paid) which feeds the Finance Calculator.',
            placement: 'left',
            clickBefore: [
                '.fc-modal-close-btn',
                '.action-button-container .action-button',
                '.menu-item.invoice-button',
            ],
            skipIfMissing: true,
        },
        {
            target: '.delivery-logs-modal',
            title: 'Logs',
            content:
                'Full audit log of every change to the delivery: who edited which field, when, and what the old and new values were. Useful for compliance, dispute resolution and operational hand-overs.',
            placement: 'center',
            clickBefore: [
                '.invoice-panel .close-button',
                '.action-button-container .action-button',
                '.menu-item.logs-button',
            ],
            skipIfMissing: true,
        },
        {
            target: '.delivery-screen .header-actions .btn-secondary',
            title: 'Save Draft',
            content:
                'Saves what you have so far without advancing. Drafts let you build up a delivery over multiple sessions while you wait for vessel confirmations.',
            placement: 'bottom',
            // Close the Logs modal before returning attention to the
            // header save buttons.
            clickBefore: '.delivery-logs-close-button',
        },
        {
            target: '.delivery-screen .header-actions .btn-primary',
            title: 'Save & Workflow',
            content:
                'Saves AND advances the delivery to the next status (e.g. Scheduled to In-Transit). Use this once vessel and dates are locked in.',
            placement: 'bottom',
        },
        {
            target: '.delivery-screen',
            title: 'You are good to go',
            content:
                'That is the delivery flow. From here the Shipment Overview map tracks the lift in real time, and the financials module picks up settlement once the delivery completes. Re-run this tour any time from Hamster Training.',
            placement: 'center',
            disableBeacon: true,
            isLast: true,
        },
    ],
};

export default deliveryTour;
